import { decodeMsg, decodeOptions, useMessages } from 'gt-react';

/**
 * Decoding for the `msg()`-encoded strings that route metadata carries.
 *
 * Route metadata is plain data, so `<T>` cannot go in it: `NavMeta.label` is
 * typed `label?: string`, and a breadcrumb is whatever a route's loader returns
 * as `crumb`. `msg()` fits — it packs the source text and its translation
 * metadata into a string — but only if the render site decodes it, which is
 * what this module is for.
 *
 * Two kinds of value reach those render sites, and they must be told apart:
 *
 *   - Authored UI copy, encoded with `msg(...)`. Translate it.
 *   - Runtime data — a customer name, a product SKU, a sidebar section the user
 *     renamed. Render it verbatim.
 *
 * Decoding the second kind is not merely pointless, it is unsafe. `m()` runs its
 * input through ICU message formatting, so a supplier named `Acme {Corp}` throws
 * there ("The intl string context variable "Corp" was not provided") and takes
 * the whole sidebar or breadcrumb bar down with it. Every decode below is
 * therefore guarded on the value actually carrying the `msg()` encoding.
 */
function isEncodedMessage(value: string): boolean {
  const options = decodeOptions(value);
  return Boolean(options?.$_hash && options?.$_source);
}

/**
 * Decodes a `msg()`-encoded route string to its **source-locale** text, passing
 * anything else through untouched.
 *
 * For code that has no React component to hang a hook on — comparators, route
 * loaders. Prefer {@link useRouteMessage} wherever a hook is available: this
 * one cannot reach the active locale.
 */
export function decodeRouteMessage(value: string): string {
  return isEncodedMessage(value) ? decodeMsg(value) : value;
}

/**
 * The render-site decoder: a `msg()`-encoded route string translated into the
 * active locale, and anything else returned as-is.
 *
 * Returned unmemoized. The React Compiler runs over this workspace and caches
 * the closure on `m`, so callers that keep it in a dependency array — the
 * `useMemo` in `useQuickSearchPages`, the `useCallback` in `useNavEntryLabel` —
 * still see a stable identity. A hand-written `useCallback` here would only
 * duplicate that.
 */
export function useRouteMessage(): (value: string) => string {
  const m = useMessages();

  return (value: string) => (isEncodedMessage(value) ? m(value) : value);
}
